import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { format, isDate, isValid, startOfDay, parseISO } from 'date-fns';
import { API_DATE_FORMAT } from 'lib/constants';
import React from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { FontFamilyRegular, theme } from 'lib/theme';
import { BUTTON_HEIGHT } from 'components/Button';
import { TEXT_SIZE_NORMAL } from 'components/Txt';
import { formatDate } from 'lib/riskbase';
import { useForceUpdate } from 'hooks/useForceUpdate';

const DateFieldWidth = 120;

interface Props {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  min?: Date;
  max?: Date;
  clearable?: boolean;
  showClearButton?: boolean;
  placeholder?: string;
  style?: StyleProp<ViewStyle>;
  showFieldTypeIcon?: boolean;
  shrinkToWidth?: boolean;
  smallGap?: boolean;
}

export const DateField: React.FC<Props> = React.memo(
  ({
    label,
    onChange,
    placeholder,
    min,
    max,
    clearable = true,
    showClearButton = true,
    value: externalValue,
    style,
    showFieldTypeIcon = true,
    shrinkToWidth = false,
    smallGap = false,
  }) => {
    const forceUpdate = useForceUpdate();
    const internalValueRef = React.useRef<string>(externalValue);
    const externalValueRef = React.useRef<string>(externalValue);

    // Reset internalValue if there is a new external value
    if (externalValueRef.current !== externalValue) {
      externalValueRef.current = externalValue;
      internalValueRef.current = externalValue;
    }

    // Return a valid value for onChange and wasModified
    const getInternalDateData = React.useCallback((): {
      /**
       * A valid value that could be passed to onChange
       */
      value: string;
      /**
       * True if internalValue is a valid date to return to onChange
       * - Empty string and field is clearable or
       * - Value is a valid date and was not modified by min or max values
       */
      internalValueIsValid: boolean;
    } => {
      if (!internalValueRef.current) {
        if (clearable) {
          return {
            value: '',
            internalValueIsValid: true,
          };
        }
        // Use last valid value (which is external value)
        return {
          value: externalValue,
          internalValueIsValid: false,
        };
      }
      let newDate = parseISO(internalValueRef.current);
      if (isDate(newDate) && isValid(newDate)) {
        // Date is valid
        let internalValueIsValid = true;
        if (min && newDate < startOfDay(min)) {
          internalValueIsValid = false;
          newDate = startOfDay(min);
        }
        if (max && newDate > startOfDay(max)) {
          internalValueIsValid = false;
          newDate = startOfDay(max);
        }
        return {
          value: format(newDate, API_DATE_FORMAT),
          internalValueIsValid,
        };
      }
      // Use last valid value (which is external value)
      return {
        value: externalValue,
        internalValueIsValid: false,
      };
    }, [clearable, externalValue, max, min]);

    const setValue = React.useCallback(
      (newValue: string) => {
        internalValueRef.current = newValue;
        const dateData = getInternalDateData();
        if (dateData.internalValueIsValid) {
          // Call onChange which should re-render component
          onChange(dateData.value);
        } else {
          // Re-render component with new value
          forceUpdate();
        }
      },
      [forceUpdate, getInternalDateData, onChange],
    );

    const handleChange = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setValue(event.target.value);
      },
      [setValue],
    );

    const handleBlur = React.useCallback(() => {
      const dateData = getInternalDateData();
      if (dateData.value !== externalValue) {
        onChange(dateData.value);
      } else if (!dateData.internalValueIsValid) {
        // Reset internal value to a valid value
        internalValueRef.current = dateData.value;
        // Re-render component
        forceUpdate();
      }
    }, [externalValue, forceUpdate, getInternalDateData, onChange]);

    const shouldShowClearButton =
      internalValueRef.current && clearable && showClearButton;

    return (
      <CardRow
        style={[
          {
            backgroundColor: theme.colors.white,
          },
          style,
        ]}
        contentStyle={[
          shrinkToWidth
            ? { flex: undefined, alignItems: 'flex-start' }
            : undefined,
          {
            paddingVertical: 0,
          },
        ]}
        label={label}
        actionIcon={ICONS.CircleXmarkLight}
        onAction={shouldShowClearButton ? () => setValue('') : undefined}
        smallActionGap={smallGap}
        navigationType={showFieldTypeIcon ? 'date-picker' : undefined}
      >
        <input
          type="date"
          onChange={handleChange}
          onBlur={handleBlur}
          value={internalValueRef.current}
          placeholder={placeholder}
          min={min ? formatDate(min, API_DATE_FORMAT) : undefined}
          max={max ? formatDate(max, API_DATE_FORMAT) : undefined}
          required={!clearable}
          style={{
            height: BUTTON_HEIGHT - 2,
            borderColor: theme.colors.blue,
            borderWidth: 1,
            borderStyle: 'solid',
            borderRadius: theme.cardBorderRadius,
            paddingRight: 4,
            paddingLeft: 4,
            fontFamily: FontFamilyRegular,
            fontSize: TEXT_SIZE_NORMAL,
            width: DateFieldWidth - 10,
          }}
        />
      </CardRow>
    );
  },
);
